import React from 'react';
import _ from 'lodash';

import {
  Box,
  Button,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import {
  Add,
  ImportExport,
  Merge
} from '@mui/icons-material';
import {
  Form,
  fields,
  modalUtil
} from 'iron-forge-react-modules';

import { CustomSpeedDial } from 'modules';
import { useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import * as ApplyTemplateToStoreModal from './ApplyTemplatesToStoreModal/ApplyTemplatesToStoreModal';
import * as FloatingStoreSelect from './FloatingStoreSelect/FloatingStoreSelect';
import * as ItemDetailDrawer from './ItemDetailDrawer/ItemDetailDrawer';
import * as ListItemModal from './ListItemModal/ListItemModal';
import * as dataService from './dataService';
import useRouteLocation from 'modules/layer00/hooks/useRouteLocation';



const ControlledTextField = fields.TextField.genControllerComponent<IFormState>();

export interface IFormState {
  searchText: string;
}


export const FC: React.FC = () => {
  const [search, setSearch] = useSearchParams();
  // getting route location
  const { isMatched: fromPartner } = useRouteLocation('partner');
  const selectedStoreId = search.get('storeId');
  const selectedItemId = search.get('itemId');
  const searchText = search.get('search') ?? '';
  const form = useForm<IFormState>({
    defaultValues: {
      searchText
    }
  });
  const {
    queryTuple,
    fnQuery
  } = dataService.useQuery();
  const [exportItemList] = dataService.useExportItemListQuery();
  const {
    modalType2IsOpen,
    setModalIsOpen
  } = modalUtil.useModalTrackers(['listItems', 'applyTemplateToStore']);



  React.useEffect(
    () => {
      if (selectedStoreId) {
        fnQuery({
          filter: {
            storeId: selectedStoreId
          }
        });

      } else {
        fnQuery({
          filter: {}
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedStoreId]
  );



  const handleClickRow = React.useCallback(
    (itemId: string) => {
      if (itemId === selectedItemId) {
        setSearch(prev => {
          prev.delete('itemId');
          return prev;
        });

      } else {
        setSearch(prev => {
          prev.set('itemId', itemId);
          return prev;
        });
      }
    },
    [selectedItemId, setSearch]
  );

  const handleClickOptionId = React.useCallback(
    (optionId: string) => {
      if (optionId === 'add') {
        setModalIsOpen('listItems', true);

      } else if (optionId === 'export') {
        exportItemList({ storeId: selectedStoreId || undefined });
      } else if (optionId === 'applyTemplate') {
        setModalIsOpen('applyTemplateToStore', true);
      }
    },
    [
      exportItemList,
      selectedStoreId,
      setModalIsOpen
    ]
  );



  const processSubmit = React.useCallback(
    (formState: IFormState) => {
      setSearch(prev => {
        if (formState.searchText) {
          prev.set('search', formState.searchText);
        } else {
          prev.delete('search');
        }
        return prev;
      });
    },
    [setSearch]
  );

  const getFormattedDate = (date1: string) => {
    if (!date1) return '';
    const date = new Date(date1);
    type Options = {
      timeZone: string;
      year: "numeric";
      month: "2-digit";
      day: "2-digit";
      hour: "2-digit";
      minute: "2-digit";
      second: "2-digit";
      hour12: boolean;
    };
    const options: Options = {
      timeZone: "America/New_York", // Eastern Time (US & Canada)
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true
    };

    // Format the date and time using the specified options
    const formattedDateTime = new Intl.DateTimeFormat("en-US", options).format(
      date
    );
    return formattedDateTime;
  };


  const rows = React.useMemo(
    () => queryTuple.loading ?
      _.times(50).map(i => (
        <TableRow key={i}>
          <TableCell><Skeleton /></TableCell>
          <TableCell><Skeleton /></TableCell>
          <TableCell><Skeleton /></TableCell>
          <TableCell><Skeleton /></TableCell>
          <TableCell><Skeleton /></TableCell>
          <TableCell><Skeleton /></TableCell>
          <TableCell><Skeleton /></TableCell>
          <TableCell><Skeleton /></TableCell>
          <TableCell><Skeleton /></TableCell>
        </TableRow>
      )) :
      queryTuple.data.items.filter(item => {
        const searchMatch = !searchText || (
          _.includes(_.toLower(item.sku), searchText.toLowerCase()) ||
          _.includes(_.toLower(item.name), searchText.toLowerCase())
        );
        return searchMatch;
      }).map(item => (
        <TableRow
          key={item.id}
          onClick={!fromPartner ? _.partial(handleClickRow, item.id) : undefined}
          sx={{ cursor: 'pointer' }}>
          <TableCell>{item.name}</TableCell>
          <TableCell>{item.sku}</TableCell>
          <TableCell>{item.defaultPriceDollars.toFixed(2)}</TableCell>
          <TableCell>{item.defaultWholesalePriceDollars.toFixed(2)}</TableCell>
          <TableCell>{item.defaultCostDollars.toFixed(2)}</TableCell>
          <TableCell>{item.quantity}</TableCell>
          <TableCell>{item.listingManagementStrategyCount}</TableCell>
          <TableCell>{getFormattedDate(item.itemLastModifiedDate)}</TableCell>
          <TableCell>{getFormattedDate(item.itemQtyLastModifiedDate)}</TableCell>
        </TableRow>
      )),
    [
      handleClickRow,
      searchText,
      queryTuple.data.items,
      queryTuple.loading,
      fromPartner
    ]
  );

  return (
    <>
      <Form.FC onSubmit={form.handleSubmit(processSubmit)}>
        <Stack
          direction="row"
          alignItems="center">
          <Box>
            <ControlledTextField
              disableDefaultMargins
              formControl={form.control}
              fieldKey="searchText" />
          </Box>

          <Button type="submit">
            Search
          </Button>

          <Box ml={2}>
            {queryTuple.loading ? (
              <Skeleton sx={{ minWidth: 100 }} />

            ) : (
              <Typography
                variant="caption"
                margin={0}>
                {queryTuple.data.items.length} items
              </Typography>
            )}
          </Box>
        </Stack>
      </Form.FC>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>SKU</TableCell>
              <TableCell>Price</TableCell>
              <TableCell>Wholesale Price</TableCell>
              <TableCell>Cost</TableCell>
              <TableCell>Quantity</TableCell>
              <TableCell># Stores</TableCell>
              <TableCell>Last Item Modified</TableCell>
              <TableCell>Last Quantity Modified</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {rows}
          </TableBody>
        </Table>
      </TableContainer>

      {selectedItemId && (
        <ItemDetailDrawer.FC
          itemId={selectedItemId}
          onClose={() => {
            setSearch(prev => {
              prev.delete('itemId');
              return prev;
            });
          }} />
      )}

      <FloatingStoreSelect.FC
        selectedStoreId={selectedStoreId ?? ''}
        onChange={id => {
          if (id) {
            setSearch(prev => {
              prev.set('storeId', id);
              return prev;
            });
          } else {
            setSearch(prev => {
              prev.delete('storeId');
              return prev;
            });
          }
        }} />

      {!queryTuple.loading && (
        <CustomSpeedDial.FC
          onClickOption={handleClickOptionId}
          options={[
            {
              id: 'add',
              label: "Add",
              show: !!selectedStoreId,
              elIcon: <Add />
            },
            {
              id: 'export',
              label: "Export",
              elIcon: <ImportExport />
            },
            {
              id: 'applyTemplate',
              label: "Apply Template",
              show: !!selectedStoreId,
              elIcon: <Merge />
            }
          ]} />
      )}

      {modalType2IsOpen.listItems && selectedStoreId && (
        <ListItemModal.FC
          storeId={selectedStoreId}
          onClose={() => setModalIsOpen('listItems', false)} />
      )}

      {modalType2IsOpen.applyTemplateToStore && selectedStoreId && (
        <ApplyTemplateToStoreModal.FC
          storeId={selectedStoreId}
          onClose={() => setModalIsOpen('applyTemplateToStore', false)} />
      )}
    </>
  );
};
